/**
 * Inverted card style — the shared renderer behind `cardStyle === "inverted"`.
 *
 * The Artisan card is painted from two roles: vellum (#E2E8F0 — the background plus the
 * low-opacity etch detail inside pips/faces) and the "ink" (pip bodies, corner labels, the
 * L-stripe, tally arcs). Inversion swaps the two everywhere: the ink floods the background,
 * every inked shape goes vellum, and the faint vellum etch picks up the ink. Applied as a
 * post-build pass on a rendered card SVG, so `createCard`'s classic path stays untouched and
 * both the live game (via `cardStyle`) and `/card-lab` share this one copy — no drift.
 *
 * A plain card's ink is its suit hex; every trump card's ink (Jacks included) is a purple<->suit
 * gradient flooding the full card, run along an axis. The swap rule is uniform — vellum -> ink,
 * any other colored paint -> vellum — so it needs no per-element case analysis. The lab drives the
 * axis live from its dropdowns; the game uses the settled INVERTED_TRUMP_AXIS below.
 */
import { TRUMP_COLOR } from "../constants.js";
import { s } from "./card-svg.js";
import { SUIT_COLORS } from "./cards.js";
import { CARD_TRUMP_GRADIENTS } from "./card-gradients.js";

export const VELLUM = "#E2E8F0";

// Card SVG geometry (viewBox 0 0 250 350) + golden-ratio divisions of each dimension.
const CARD_W = 250;
const CARD_H = 350;
const PHI = (1 + Math.sqrt(5)) / 2;
const PHI_INV = 1 / PHI; // 0.618…
const PHI_INV2 = 1 / (PHI * PHI); // 0.382…

// Gradient axes for the inverted-trump background, as {x1,y1 -> x2,y2} in card user space. The
// stops always run purple (offset 0%) at the start point to suit color at the end. Each axis'
// midpoint is the card center, so the transition crosses the center in every mode. The two simple
// diagonals run corner-to-corner: "diagonal" TL->BR, "antiDiagonal" BL->TR. The two golden
// diagonals share the TL->BR direction but place endpoints at the golden-ratio division of one
// dimension: "goldenLength" splits the height across the left/right edges, "goldenWidth" splits the
// width across the top/bottom edges.
export const GRADIENT_AXES = {
  vertical: { x1: 0, y1: 0, x2: 0, y2: CARD_H },
  horizontal: { x1: 0, y1: 0, x2: CARD_W, y2: 0 },
  diagonal: { x1: 0, y1: 0, x2: CARD_W, y2: CARD_H },
  antiDiagonal: { x1: 0, y1: CARD_H, x2: CARD_W, y2: 0 },
  goldenLength: {
    x1: 0,
    y1: CARD_H * PHI_INV2,
    x2: CARD_W,
    y2: CARD_H * PHI_INV,
  },
  goldenWidth: {
    x1: CARD_W * PHI_INV2,
    y1: 0,
    x2: CARD_W * PHI_INV,
    y2: CARD_H,
  },
};

// The settled axis for the inverted trump flood in the live game — THE one-line tuning knob for the
// trump look. Candidates Andre flagged as the appeal are the diagonals ("diagonal", "antiDiagonal")
// and the goldens ("goldenLength", "goldenWidth"); diamonds especially shine on a diagonal. Preview
// any of them live on /card-lab (Color mode → Inverted, Split axis), then bake the winner here.
export const INVERTED_TRUMP_AXIS = "goldenWidth";

let invGradCounter = 0; // unique ids for the inversion gradients

// The named axis as a {x1,y1 -> x2,y2} vector, optionally flipped (swaps which end is purple).
export function axisVector(name, flip = false) {
  const a = GRADIENT_AXES[name];
  return flip ? { x1: a.x2, y1: a.y2, x2: a.x1, y2: a.y1 } : a;
}

// A userSpaceOnUse linearGradient element + its url, for the given axis and stops.
export function buildGradientEl(axis, stops) {
  const id = `inv-grad-${invGradCounter++}`;
  const lg = s("linearGradient", {
    id,
    gradientUnits: "userSpaceOnUse",
    ...axis,
  });
  for (const { offset, color } of stops)
    lg.appendChild(s("stop", { offset, "stop-color": color }));
  return { id, lg };
}

// Build a full-card purple<->suit gradient for an inverted trump background, appended to the card's
// own SVG defs. Built fresh rather than reusing the card's master gradient so Jacks — which build no
// card gradient — get the same treatment as the other trumps.
function trumpInk(svg, suit, axis) {
  const grad = CARD_TRUMP_GRADIENTS[suit];
  if (!grad) return TRUMP_COLOR; // non-suit safety (grand is not a card suit)
  const { id, lg } = buildGradientEl(axis, grad.stops);
  const defs = s("defs");
  defs.appendChild(lg);
  svg.appendChild(defs);
  return `url(#${id})`;
}

// Resolve the card's ink — the paint that floods the background under inversion.
function resolveInk(cardEl, svg, options, axis) {
  if (options.isTrump) return trumpInk(svg, cardEl.dataset.suit, axis);
  return SUIT_COLORS[cardEl.dataset.suit];
}

// Swap vellum <-> ink in every fill/stroke attribute. Reads each value once and maps it in one
// pass, so the two-way swap can't double-apply. Opacities stay put — a 0.12 vellum etch on an
// inked body becomes a 0.12 inked etch on a vellum body, the mirror of the original.
//
// options: { isTrump, axis }. axis defaults to the settled INVERTED_TRUMP_AXIS; the lab passes its
// live dropdown axis.
export function invertCardColors(cardEl, options = {}) {
  const svg = cardEl.querySelector(":scope > svg");
  if (!svg) return cardEl;
  const axis = options.axis ?? axisVector(INVERTED_TRUMP_AXIS);
  const ink = resolveInk(cardEl, svg, options, axis);
  if (!ink) return cardEl;
  for (const el of svg.querySelectorAll("[fill], [stroke]")) {
    for (const attr of ["fill", "stroke"]) {
      const v = el.getAttribute(attr);
      if (v == null || v === "none") continue;
      el.setAttribute(attr, v === VELLUM ? ink : VELLUM);
    }
  }
  return cardEl;
}
